import { useReactTable } from '@tanstack/react-table';
import React from 'react';
import { match } from 'ts-pattern';

import { SimpleTooltip } from '@eluve/components';
import {
  ColDefBuilder,
  type DataTableFilterField,
  ServerDataTable,
  Stack,
  Switch,
  TableSearch,
  Tag,
  Text,
  useDataTableForQuery,
} from '@eluve/components';
import { PatientsOrderBy } from '@eluve/graphql-types';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { formatNumber } from '@eluve/utils';

import {
  buildPatientsListFilter,
  isShowingFlaggedPatients,
} from './patientsListFilter';
import { useIsPatientProfileEnabled } from './profile/PatientProfileGate';

const searchPatients = graphql(`
  query searchPatients(
    $filter: PatientsBoolExp
    $offset: Int
    $orderBy: [PatientsOrderBy!]
    $limit: Int
    $searchTerm: String
    $tenantId: uuid!
  ) {
    searchPatients(
      args: { search_term: $searchTerm, _tenant_id: $tenantId }
      where: $filter
      limit: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      __typename
      id
      firstName
      lastName
      dateOfBirth
      sex
      deceasedAt
      deactivatedAt
      external_patients_info {
        __typename
        id
        externalPatientId
        external_ehr {
          id
          __typename
          vendor
        }
      }
      metrics {
        __typename
        id
        appointmentCount
        mostRecentAppointmentStartedAt
      }
    }
    searchPatientsAggregate(
      args: { search_term: $searchTerm, _tenant_id: $tenantId }
      where: $filter
    ) {
      __typename
      aggregate {
        __typename
        count
      }
    }
  }
`);

type Row = Omit<
  ResultOf<typeof searchPatients>['searchPatients'][number],
  'appointmentsAggregate' | 'metrics'
> & {
  importedFrom: React.ReactNode | null;
  appointmentCount: number;
  lastAppointmentDate: string | null;
  isFlagged: boolean;
};

const columns = new ColDefBuilder<Row>()
  .detailsLink((r) => `${r.id}`, 'Details')
  .defaultSortable('firstName', { label: 'First Name', isPrivate: true })
  .defaultSortable('lastName', { label: 'Last Name', isPrivate: true })
  .dateSortable('lastAppointmentDate', 'Last Appointment')
  .dateSortable('dateOfBirth', { label: 'Date of Birth', isPrivate: true })
  .defaultSortable('importedFrom', 'Imported From')
  .defaultSortable('appointmentCount', {
    label: '# Appointments',
    cellRenderer: (row) => formatNumber(row.appointmentCount),
  })
  .build();

// Registered so the hook syncs the toggle to the isFlagged URL param; the
// toggle is rendered as a Switch instead of the standard faceted dropdown.
const filterFields: DataTableFilterField<Row>[] = [
  {
    id: 'isFlagged',
    label: 'Include inactive/deceased',
    options: [{ label: 'Include inactive/deceased', value: 'true' }],
  },
];

const convertSearchResultsToRows = (
  results: ResultOf<typeof searchPatients>,
): Row[] => {
  return results.searchPatients.map((p) => {
    const { metrics, ...rest } = p;
    const externalPatientInfos = p.external_patients_info;
    const vendors = externalPatientInfos
      .filter((info) => info.external_ehr?.vendor)
      .map((info) => ({
        vendor: info.external_ehr.vendor,
        externalPatientId: info.externalPatientId,
      }));

    return {
      ...rest,
      isFlagged: Boolean(p.deceasedAt || p.deactivatedAt),
      importedFrom: vendors.length ? (
        <>
          {vendors.map(({ vendor, externalPatientId }) => (
            <Stack key={vendor} gap={1} direction="horizontal">
              <SimpleTooltip label="Copy Patient External ID">
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(externalPatientId);
                  }}
                  className="cursor-pointer"
                >
                  <Tag
                    color="gray"
                    label={vendor}
                    size="xs"
                    className="text-xs font-medium text-text-primary"
                  />
                </div>
              </SimpleTooltip>
            </Stack>
          ))}
        </>
      ) : null,
      appointmentCount: metrics?.appointmentCount ?? 0,
      lastAppointmentDate: metrics?.mostRecentAppointmentStartedAt ?? null,
    };
  });
};

export const PatientsList: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const canMarkPatientStatus = useIsPatientProfileEnabled();
  const {
    data,
    rows,
    reactTableOptions,
    setSearch,
    search,
    isPending,
    filters,
  } = useDataTableForQuery({
    query: searchPatients,
    filterFields,
    convertToRows: convertSearchResultsToRows,
    convertSearchParamsToVariables: ({
      offset,
      limit,
      search,
      sorting,
      filters,
    }) => ({
      offset,
      limit,
      tenantId,
      searchTerm: search,
      filter: buildPatientsListFilter({
        showFlagged: isShowingFlaggedPatients(filters),
      }),
      orderBy:
        sorting && sorting.length
          ? sorting.map(({ desc, id }) => ({
              ...match<keyof Row, PatientsOrderBy>(id)
                .with('lastAppointmentDate', () => ({
                  metrics: {
                    mostRecentAppointmentStartedAt: desc
                      ? 'DESC_NULLS_LAST'
                      : 'ASC',
                  },
                }))
                .with('appointmentCount', () => ({
                  metrics: {
                    appointmentCount: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  },
                }))
                .with('importedFrom', () => ({
                  external_patients_infoAggregate: {
                    count: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  },
                }))
                .otherwise(() => ({
                  [id]: desc ? 'DESC_NULLS_LAST' : 'ASC',
                })),
            }))
          : null,
    }),
  });

  const rowCount = data?.searchPatientsAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    ...reactTableOptions,
  });

  const showFlagged = isShowingFlaggedPatients(filters);

  const onShowFlaggedChange = (checked: boolean) => {
    table.setColumnFilters(
      checked ? [{ id: 'isFlagged', value: ['true'] }] : [],
    );
  };

  return (
    <Stack className="rounded-md border p-2">
      <Stack justify="between" gap={2} direction="horizontal">
        <TableSearch value={search ?? ''} onChange={setSearch} />
        {canMarkPatientStatus && (
          <Stack
            direction="horizontal"
            gap={2}
            align="center"
            fullWidth={false}
            className="shrink-0"
          >
            <Switch
              id="include-flagged-patients"
              checked={showFlagged}
              onCheckedChange={onShowFlaggedChange}
              aria-label="Include inactive/deceased"
            />
            <label
              htmlFor="include-flagged-patients"
              className="cursor-pointer"
            >
              <Text size="sm">Include inactive/deceased</Text>
            </label>
          </Stack>
        )}
      </Stack>
      <ServerDataTable<Row> table={table} isPending={isPending} />
    </Stack>
  );
};
